import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import _ from 'lodash';
import { Box, Button, IconButton, Skeleton, Typography } from '@mui/material';
import { ReactComponent as DateRangeIcon } from '../../static/images/svg/ic_round-date-range.svg';
import { ReactComponent as MingcuteCloseFillIcon } from '../../static/images/svg/mingcute_close-fill.svg';
import i18n from '../../i18n';
import { apiManage_rbp } from '../../request';
import GlobalNotification from '../../component/common/snackbar';
import setLoading from '../../component/linear-determinate';
import ButtonComponent from '../../component/button/button.component';
import CommonInput from '../../component/form/common-input.component';
import { RBP_MENU_SETTINGS } from '../../constants/rbp-menu-settings.constant';
import './scss/park-mode-booking-time.scss';
import { ReactComponent as AddIcon } from '../../static/images/svg/mingcute_add-fill.svg';

/**
 * 娱乐项目预订时间：读写 settings/149 的 `stringValue`（逗号分隔 HH:mm），与 RBP2 一致。
 */
const AUTO_SAVE_DEBOUNCE_MS = 250;

/** 加载占位：药丸行数量 */
const BOOKING_TIME_SKELETON_PILL_COUNT = 3;

const ParkModeBookingTimeTabPage: React.FC = () => {
    /** 当前编辑中的时间点字符串列表 */
    const [metaData, setMetaData] = useState<string[]>([]);
    /** 时段列表接口未返回前展示骨架（含再次 load，如同步后刷新） */
    const [slotsLoading, setSlotsLoading] = useState(true);
    /** 与后端已一致的 `stringValue`，用于跳过无变更的 PATCH */
    const persistedKeyRef = useRef<string | null>(null);
    /** 初次从接口灌入完成前不自动保存 */
    const allowAutoPersistRef = useRef(false);

    const buildSortedTimeStrings = useCallback((list: string[]) => {
        const metaDataFilter = _.uniq(list);
        const timeList = metaDataFilter.map((item) => moment(item, 'HH:mm'));
        const timeListFilter = timeList.filter((item) => item.isValid());
        const timeListFilterSort = _.sortBy(timeListFilter);
        return timeListFilterSort.map((item) => item.format('HH:mm'));
    }, []);

    const persistBookingTimes = useCallback(
        async (list: string[]) => {
            const params = buildSortedTimeStrings(list);
            const stringValue = params.join(',');
            if (stringValue === persistedKeyRef.current) {
                return;
            }
            const settingResult = await apiManage_rbp.patchSettingById(RBP_MENU_SETTINGS.PARK_MODE_BOOKING_TIME, {
                stringValue,
            });
            if (_.get(settingResult, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            persistedKeyRef.current = stringValue;
            GlobalNotification.success(i18n.t('google_text_public_text_save_successfully'));
        },
        [buildSortedTimeStrings],
    );

    const load = useCallback(async () => {
        allowAutoPersistRef.current = false;
        setSlotsLoading(true);
        setLoading(true);
        try {
            const result = await apiManage_rbp.getSettingById(RBP_MENU_SETTINGS.PARK_MODE_BOOKING_TIME);
            const value = _.get(result, '[0].value', '');
            const nextMeta =
                !result.error && value && typeof value === 'string' ? value.split(',') : [];
            setMetaData(nextMeta);
            persistedKeyRef.current = buildSortedTimeStrings(nextMeta).join(',');
            allowAutoPersistRef.current = true;
        } finally {
            setSlotsLoading(false);
            setLoading(false);
        }
    }, [buildSortedTimeStrings]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        if (!allowAutoPersistRef.current) {
            return;
        }
        const timer = window.setTimeout(() => {
            void persistBookingTimes(metaData);
        }, AUTO_SAVE_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [metaData, persistBookingTimes]);

    const handleChangeTime = (date: Date | null, index: number) => {
        const next = [...metaData];
        next[index] = date ? moment(date).format('HH:mm') : '';
        setMetaData(next);
    };

    /** 从列表中移除该时间点（整枚药丸） */
    const handleRemoveSlot = (index: number) => {
        setMetaData((prev) => prev.filter((_, i) => i !== index));
    };

    const handleAdd = () => {
        setMetaData([...metaData, '']);
    };

    const handleSync = async () => {
        setLoading(true);
        const result = await apiManage_rbp.synchronization_amusement_time();
        if (result?.error) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } else {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            await load();
        }
        setLoading(false);
    };

    return (
        <Box className="park-mode-booking-time">
            <Box className="park-mode-booking-time__section">
                <Box className="park-mode-booking-time__section-head">
                    <Typography variant="h5" className="park-mode-booking-time__page-title">
                        {i18n.t('park_mode_page_text_booking_time_management')}
                    </Typography>
                    <Box className="park-mode-booking-time__toolbar">
                        <ButtonComponent
                            variant="outlined"
                            disabled={slotsLoading}
                            handleClick={() => void handleSync()}
                        >
                            {i18n.t('park_mode_page_btn_sync_booking_time')}
                        </ButtonComponent>
                    </Box>
                </Box>
                <Box className="park-mode-booking-time__rows" aria-busy={slotsLoading}>
                    {slotsLoading ? (
                        <BookingTimeSkeleton />
                    ) : (
                        <>
                            {metaData.map((item, index) => (
                                <Box key={index} className="park-mode-booking-time__slot-pill">
                                    <Box className="park-mode-booking-time__slot-inner">
                                        <Box className="park-mode-booking-time__slot-leading" aria-hidden>
                                            <DateRangeIcon width={24} height={24} />
                                        </Box>
                                        <Box className="park-mode-booking-time__time-field">
                                            <CommonInput
                                                inputType="TIME_COMPACT"
                                                variant="outlined"
                                                fullWidth={false}
                                                size="medium"
                                                value={item ? moment(item, 'HH:mm').toDate() : undefined}
                                                handleChangePicker={(d) => handleChangeTime(d, index)}
                                                inputProps={{
                                                    'aria-label': i18n.t('park_mode_page_label_booking_slot'),
                                                }}
                                            />
                                        </Box>
                                        <IconButton
                                            type="button"
                                            size="small"
                                            className="park-mode-booking-time__slot-clear"
                                            aria-label={i18n.t('general_text_delete')}
                                            title={i18n.t('general_text_delete')}
                                            onClick={() => handleRemoveSlot(index)}
                                        >
                                            <MingcuteCloseFillIcon width={24} height={24} />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))}
                            <Button
                                type="button"
                                variant="text"
                                className="park-mode-booking-time__add-pill"
                                onClick={handleAdd}
                                aria-label={i18n.t('park_mode_page_btn_add_booking_time')}
                            >
                                <span className="park-mode-booking-time__add-pill-label">
                                    {i18n.t('park_mode_page_btn_add_booking_time')}
                                </span>
                                <span className="park-mode-booking-time__add-pill-icon" aria-hidden>
                                    <AddIcon width={24} height={24} />
                                </span>
                            </Button>
                        </>
                    )}
                </Box>
            </Box>
        </Box >
    );
};

export default ParkModeBookingTimeTabPage;


function BookingTimeSkeleton() {
    return (
        <>
            {Array.from({ length: BOOKING_TIME_SKELETON_PILL_COUNT }).map((_, i) => (
                <Skeleton
                    key={`booking-sk-pill-${i}`}
                    variant="rounded"
                    animation="wave"
                    width={200}
                    height={44}
                    sx={{ borderRadius: '24px', flexShrink: 0 }}
                />
            ))}
        </>
    );
}